import { Delete, RefreshOutlined, Timeline, MoreVert, PlayArrow, Search, Clear, LinkedIn, Place } from "@mui/icons-material";
import { Bot } from "lucide-react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import axios from "axios";
import moment from "moment";
import { useEffect, useState } from "react";
import { formatStep, hostname, parseError, isSafeUrl } from "../../../utils";
import {
  SEQUENCE_MEMBER_STATUS_ACTIVE,
  SEQUENCE_MEMBER_STATUS_UNSUBSCRIBED,
  SEQUENCE_MEMBER_STATUS_BOUNCED,
  SEQUENCE_MEMBER_STATUS_PAUSED,
  SEQUENCE_MEMBER_STATUS_REMOVED,
  AUTO_AGENT_STATUS_ACTIVE,
  AUTO_AGENT_STATUS_GOAL_MET,
  AUTO_AGENT_STATUS_MAX_STEPS,
  AUTO_AGENT_STATUS_STOPPED,
} from "../../../constants";
import SequenceMemberAddDialog from "../SequenceMemberAddDialog";
import SequenceMemberTimelineDialog from "../SequenceMemberTimelineDialog";
import SequenceMemberProfileDialog from "../dialogs/SequenceMemberProfileDialog";

interface SequenceMembersViewProps {
  companyId: string;
  sequence?: any;
  getToken: () => Promise<string>;
  eventListener?: Function;
  showTitle?: boolean;
  showActions?: boolean;
}

export default ({
  companyId,
  sequence,
  getToken,
  eventListener,
  showTitle = true,
  showActions = true
}: SequenceMembersViewProps) => {
  const [members, setMembers] = useState<Array<any>>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedMember, setSelectedMember] = useState<any>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [timelineDialogOpen, setTimelineDialogOpen] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [triggeringMember, setTriggeringMember] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const handleStatusFilterChange = (event: any) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const refreshMembers = async () => {
    if (!sequence?.id) return;

    setRefreshing(true);
    const currentToken = await getToken();

    const params: any = {
      companyId,
      page: page + 1,
      pageSize,
    };

    if (debouncedSearch) params.search = debouncedSearch;
    params.status = statusFilter;

    axios
      .get(`${hostname}/sequences/${sequence.id}/members`, {
        params,
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      })
      .then((res) => {
        setMembers(res.data.items || []);
        setTotal(res.data.total || 0);
      })
      .catch((err) => {
        if (eventListener) {
          eventListener({
            type: "SET_ERROR",
            error: parseError(err),
          });
        }
      })
      .finally(() => {
        setRefreshing(false);
      });
  };

  const loadMembers = async () => {
    setLoading(true);
    await refreshMembers();
    setLoading(false);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, member: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedMember(member);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedMember(null);
  };

  const triggerMember = async (memberId: string) => {
    setTriggeringMember(true);
    const currentToken = await getToken();
    await axios
      .post(
        `${hostname}/sequences/${sequence.id}/members/${memberId}/trigger`,
        { companyId },
        {
          headers: { Authorization: `Bearer ${currentToken}` },
        }
      )
      .then(() => {
        refreshMembers();
      })
      .catch((err) => {
        if (eventListener) {
          eventListener({
            type: "SET_ERROR",
            error: parseError(err),
          });
        }
      })
      .finally(() => {
        setTriggeringMember(false);
      });
    handleMenuClose();
  };

  const removeMember = async (memberId: string, reason: string = "manual") => {
    try {
      const currentToken = await getToken();
      await axios.post(
        `${hostname}/sequences/${sequence.id}/members/${memberId}/remove`,
        { companyId, reason },
        {
          headers: { Authorization: `Bearer ${currentToken}` },
        }
      );
      refreshMembers();
    } catch (err) {
      if (eventListener) {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      }
      throw err; // Re-throw to handle in calling function
    }
  };

  const handleRemoveClick = () => {
    setDeleteConfirmOpen(true);
    setAnchorEl(null); // Close menu but keep selectedMember
  };

  const handleConfirmRemove = async () => {
    if (selectedMember) {
      try {
        await removeMember(selectedMember.id);
        setDeleteConfirmOpen(false);
        setSelectedMember(null);
      } catch (err) {
        // Error already handled in removeMember, just close the dialog
        setDeleteConfirmOpen(false);
      }
    }
  };

  const handleCancelRemove = () => {
    setDeleteConfirmOpen(false);
    setSelectedMember(null); // Clear selected member on cancel
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case SEQUENCE_MEMBER_STATUS_ACTIVE:
        return "primary";
      case SEQUENCE_MEMBER_STATUS_PAUSED:
        return "warning";
      case SEQUENCE_MEMBER_STATUS_UNSUBSCRIBED:
      case SEQUENCE_MEMBER_STATUS_BOUNCED:
      case SEQUENCE_MEMBER_STATUS_REMOVED:
        return "error";
      default:
        return "default";
    }
  };

  // Helper to get auto agent status info
  const getAutoAgentStatusInfo = (autoAgentState: any) => {
    // Show auto agent info if there's any state (active or completed)
    if (!autoAgentState) return null;
    // Check if session was ever started (has a status or is active)
    if (!autoAgentState.active && !autoAgentState.status) return null;

    const status = autoAgentState.status || AUTO_AGENT_STATUS_ACTIVE;
    const currentStep = autoAgentState.currentStepCount || 0;
    const maxSteps = autoAgentState.maxSteps || 5;
    const goal = autoAgentState.goal;
    const nextFollowUp = autoAgentState.nextFollowUpDate;

    // Count replies from conversation history
    const conversationHistory = autoAgentState.conversationHistory || [];
    const replyCount = conversationHistory.filter(
      (msg: any) => msg.role === "contact"
    ).length;
    const messagesSent = conversationHistory.filter(
      (msg: any) => msg.role === "agent"
    ).length;

    let statusLabel = "Active";
    let statusColor: "primary" | "secondary" | "warning" | "error" | "info" | "default" = "secondary";

    switch (status) {
      case AUTO_AGENT_STATUS_ACTIVE:
        statusLabel = `Active (${currentStep}/${maxSteps})`;
        statusColor = replyCount > 0 ? "primary" : "secondary";
        break;
      case AUTO_AGENT_STATUS_GOAL_MET:
        statusLabel = "Goal Met";
        statusColor = "primary";
        break;
      case AUTO_AGENT_STATUS_MAX_STEPS:
        statusLabel = "Max Steps";
        statusColor = "warning";
        break;
      case AUTO_AGENT_STATUS_STOPPED:
        statusLabel = "Stopped";
        statusColor = "default";
        break;
    }

    return {
      status,
      statusLabel,
      statusColor,
      currentStep,
      maxSteps,
      goal,
      nextFollowUp,
      goalMet: autoAgentState.goalMet,
      goalMetReason: autoAgentState.goalMetReason,
      replyCount,
      messagesSent,
    };
  };

  const clearSearch = () => {
    setSearch("");
    setDebouncedSearch("");
    setPage(0);
  };

  // Debounce search input
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0); // Reset to first page on search
    }, 500); // 500ms debounce

    return () => clearTimeout(delayDebounceFn);
  }, [search]);

  useEffect(() => {
    if (sequence?.id) {
      refreshMembers();
    }
  }, [sequence?.id, page, pageSize, debouncedSearch, statusFilter]);

  useEffect(() => {
    if (sequence?.id) {
      loadMembers();
    }
  }, [sequence?.id]);

  if (!sequence) {
    return (
      <Box p={4}>
        <Typography variant="body1" color="text.secondary">
          No sequence selected.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {showTitle && (
        <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
          Sequence Members
        </Typography>
      )}
      
      {/* Search and Filters */}
      <Box sx={{ mt: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search members..."
          value={search}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: search && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={clearSearch}>
                  <Clear />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 250 }}
        />
        
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={handleStatusFilterChange}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value={SEQUENCE_MEMBER_STATUS_ACTIVE}>Active</MenuItem>
            <MenuItem value={SEQUENCE_MEMBER_STATUS_PAUSED}>Paused</MenuItem>
            <MenuItem value={SEQUENCE_MEMBER_STATUS_UNSUBSCRIBED}>Unsubscribed</MenuItem>
            <MenuItem value={SEQUENCE_MEMBER_STATUS_BOUNCED}>Bounced</MenuItem>
            <MenuItem value={SEQUENCE_MEMBER_STATUS_REMOVED}>Removed</MenuItem>
          </Select>
        </FormControl>
        
        {showActions && (
          <Button
            variant="outlined"
            onClick={() => setAddDialogOpen(true)}
            sx={{ ml: 'auto' }}
          >
            Add Member
          </Button>
        )}
      </Box>

      <Paper variant="outlined">
        {refreshing && <LinearProgress />}
        {!refreshing && <div style={{ height: 4 }}></div>}
        
        <IconButton
          onClick={refreshMembers}
          style={{ float: "right", marginRight: 5 }}
        >
          <RefreshOutlined />
        </IconButton>
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>LinkedIn</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Auto Agent</TableCell>
                <TableCell>Current Step</TableCell>
                <TableCell>Last Step Date</TableCell>
                <TableCell>Added Date</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {members.map((member) => (
                <TableRow
                  key={member.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => {
                    setSelectedMember(member);
                    setProfileDialogOpen(true);
                  }}
                >
                  <TableCell>
                    {member.firstName} {member.lastName}
                  </TableCell>
                  <TableCell>{member.email}</TableCell>
                  <TableCell>{member.phone || "-"}</TableCell>
                  <TableCell>
                    {member.linkedinUrl && (
                      <Tooltip title="LinkedIn Profile">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            const safeUrlPattern = /^https?:\/\/[a-zA-Z0-9][a-zA-Z0-9.\-:/\\?&=%_+~#]+$/;
                            if (safeUrlPattern.test(member.linkedinUrl)) window.open(member.linkedinUrl, '_blank');
                          }}
                        >
                          <LinkedIn />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    {member.address && (member.address.address || member.address.city) && (
                      <Tooltip title={`${member.address.address || ''}${member.address.address && member.address.city ? ', ' : ''}${member.address.city || ''}${member.address.state ? `, ${member.address.state}` : ''}${member.address.postalCode ? ` ${member.address.postalCode}` : ''}`}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            const addr = member.address;
                            const query = encodeURIComponent(
                              `${addr.address || ''} ${addr.city || ''} ${addr.state || ''} ${addr.postalCode || ''} ${addr.country || 'US'}`.trim()
                            );
                            window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, '_blank');
                          }}
                        >
                          <Place />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Chip
                        label={(member.status || SEQUENCE_MEMBER_STATUS_ACTIVE).charAt(0).toUpperCase() + (member.status || SEQUENCE_MEMBER_STATUS_ACTIVE).slice(1)}
                        color={getStatusColor(member.status || SEQUENCE_MEMBER_STATUS_ACTIVE) as any}
                        size="small"
                      />
                      {member.removedReason && (
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                          {member.removedReason}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const autoAgentInfo = getAutoAgentStatusInfo(member.autoAgentState);
                      if (!autoAgentInfo) return "-";
                      return (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                          <Tooltip
                            title={
                              <Box>
                                {autoAgentInfo.goal && (
                                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    <strong>Goal:</strong> {autoAgentInfo.goal}
                                  </Typography>
                                )}
                                <Typography variant="body2">
                                  <strong>Messages sent:</strong> {autoAgentInfo.messagesSent}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Replies received:</strong> {autoAgentInfo.replyCount}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Follow-ups:</strong> {autoAgentInfo.currentStep}/{autoAgentInfo.maxSteps}
                                </Typography>
                                {autoAgentInfo.goalMet && autoAgentInfo.goalMetReason && (
                                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    <strong>Result:</strong> {autoAgentInfo.goalMetReason}
                                  </Typography>
                                )}
                                {autoAgentInfo.nextFollowUp && autoAgentInfo.status === AUTO_AGENT_STATUS_ACTIVE && (
                                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    <strong>Next follow-up:</strong> {moment(autoAgentInfo.nextFollowUp).format("MMM D, h:mm A")}
                                  </Typography>
                                )}
                              </Box>
                            }
                          >
                            <Chip
                              icon={<Bot size={16} />}
                              label={autoAgentInfo.statusLabel}
                              size="small"
                              color={autoAgentInfo.statusColor}
                              variant={autoAgentInfo.status === AUTO_AGENT_STATUS_ACTIVE ? "filled" : "outlined"}
                            />
                          </Tooltip>
                        </Box>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {member.currentStep
                      ? formatStep(member.currentStep)
                      : "Not started"}
                  </TableCell>
                  <TableCell>
                    {member.lastStepDate
                      ? moment(member.lastStepDate).format("MMM D, YYYY h:mm A")
                      : "-"}
                  </TableCell>
                  <TableCell>
                    {member.created_date
                      ? moment(member.created_date).format("MMM D, YYYY")
                      : "-"}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMenuClick(e, member);
                      }}
                    >
                      <MoreVert />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={total}
            rowsPerPage={pageSize}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      </Paper>

      {/* Member Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            setTimelineDialogOpen(true);
            setAnchorEl(null); // Close menu but keep selectedMember
          }}
        >
          <Timeline sx={{ mr: 1 }} /> Timeline
        </MenuItem>
        <MenuItem
          onClick={() => triggerMember(selectedMember?.id)}
          disabled={triggeringMember}
        >
          {triggeringMember ? (
            <CircularProgress size={20} sx={{ mr: 1 }} />
          ) : (
            <PlayArrow sx={{ mr: 1 }} />
          )}{" "}
          Trigger Next Step
        </MenuItem>
        <MenuItem
          onClick={handleRemoveClick}
          sx={{ color: 'error.main' }}
        >
          <Delete sx={{ mr: 1 }} /> Remove
        </MenuItem>
      </Menu>

      {/* Add Member Dialog */}
      <SequenceMemberAddDialog
        open={addDialogOpen}
        onClose={() => {
          setAddDialogOpen(false);
          refreshMembers();
        }}
        companyId={companyId}
        sequence={sequence}
        getToken={getToken}
      />

      {/* Timeline Dialog */}
      <SequenceMemberTimelineDialog
        open={timelineDialogOpen}
        onClose={() => {
          setTimelineDialogOpen(false);
          setSelectedMember(null); // Clear selected member when dialog closes
        }}
        member={selectedMember}
        sequence={sequence}
        companyId={companyId}
        getToken={getToken}
      />

      {/* Profile Dialog */}
      <SequenceMemberProfileDialog
        open={profileDialogOpen}
        onClose={() => {
          setProfileDialogOpen(false);
          setSelectedMember(null); // Clear selected member when dialog closes
        }}
        companyId={companyId}
        sequenceId={sequence?.id}
        memberId={selectedMember?.id}
        getToken={getToken}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCancelRemove}
      >
        <DialogTitle>Confirm Remove Member</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove {selectedMember?.firstName} {selectedMember?.lastName} from this sequence? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRemove}>
            Cancel
          </Button>
          <Button onClick={handleConfirmRemove} color="error" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};